/**
 * Global market ticker strip.
 *
 * Slim status bar (paired with OscillatorBanner) rendered above the tab nav.
 * Shows the live delayed level + percent change for Dow, S&P 500, Nasdaq 100,
 * and VIX, sourced from `/api/market/indices` (which wraps the same Massive.com
 * snapshot the `get_indices` MCP tool serves to Claude).
 *
 * Color convention: equity indices are green when up / red when down. VIX is
 * inverted — a rising fear gauge is "risk-off" and shown red, a falling VIX
 * green — so the strip reads consistently as green=benign / red=stress.
 */

import { useEffect, useState } from 'react';
import { getIndices } from '../api/client';

const REFRESH_MS = 60_000;

// Per-ticker level formatting. Big equity indices read better as whole points
// with thousands separators; VIX is a low-magnitude index shown to one decimal.
function formatLevel(ticker, level) {
  if (level == null || Number.isNaN(Number(level))) return '—';
  const n = Number(level);
  if (ticker === 'I:VIX') {
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatPct(v) {
  if (v == null || Number.isNaN(Number(v))) return null;
  const n = Number(v);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}%`;
}

// Freshest print time across the quotes — what the levels are actually "as of".
// Massive's Indices Starter tier is ~15-min delayed, so this typically trails
// wall-clock by that much; surfacing it lets a genuinely stuck strip stand out.
function freshestPrint(quotes) {
  let latest = null;
  for (const q of quotes) {
    if (!q.last_updated) continue;
    const t = new Date(q.last_updated).getTime();
    if (!Number.isNaN(t) && (latest == null || t > latest)) latest = t;
  }
  return latest;
}

function formatAsOf(ts) {
  if (ts == null) return null;
  const time = new Date(ts).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
  const agoMin = Math.max(0, Math.round((Date.now() - ts) / 60000));
  return agoMin >= 1 ? `${time} (${agoMin}m ago)` : time;
}

// Direction sign for coloring. VIX inverts: up is stress (red), down is calm.
function toneFor(ticker, changePct) {
  if (changePct == null || Number.isNaN(Number(changePct)) || Number(changePct) === 0) {
    return 'flat';
  }
  const up = Number(changePct) > 0;
  const bullish = ticker === 'I:VIX' ? !up : up;
  return bullish ? 'up' : 'down';
}

const TONE_CLASS = {
  up: 'text-emerald-400',
  down: 'text-red-400',
  flat: 'text-slate-400',
};

function IndexCell({ quote }) {
  const tone = toneFor(quote.ticker, quote.change_pct);
  const pct = formatPct(quote.change_pct);
  return (
    <span className="flex items-baseline gap-1.5 whitespace-nowrap">
      <span className="font-semibold uppercase tracking-wide text-[10px] text-slate-400">
        {quote.short_name}
      </span>
      <span className="font-mono text-slate-200">
        {formatLevel(quote.ticker, quote.level)}
      </span>
      {pct && <span className={`font-mono ${TONE_CLASS[tone]}`}>{pct}</span>}
    </span>
  );
}

export default function MarketTicker({ refreshSignal = 0 }) {
  const [state, setState] = useState({ status: 'loading', quotes: [], delayed: false });

  // Re-runs on mount and whenever `refreshSignal` changes (i.e. the global
  // Refresh button was clicked), so the strip refetches in lockstep with the
  // rest of the dashboard instead of drifting on its own throttled timer.
  // Each run also resets the 60s auto-refresh interval.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const resp = await getIndices();
        if (cancelled) return;
        if (!resp.available || !resp.indices?.length) {
          setState({ status: 'empty', quotes: [], delayed: false, asOf: null });
        } else {
          setState({
            status: 'ready',
            quotes: resp.indices,
            delayed: !!resp.delayed,
            asOf: freshestPrint(resp.indices),
          });
        }
      } catch {
        if (!cancelled) setState({ status: 'error', quotes: [], delayed: false });
      }
    };
    load();
    const interval = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [refreshSignal]);

  if (state.status === 'loading') {
    return (
      <div className="h-8 border-b border-slate-700/50 bg-slate-800/20">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center text-xs text-slate-500">
          Loading indices…
        </div>
      </div>
    );
  }

  if (state.status === 'empty' || state.status === 'error') {
    return (
      <div className="h-8 border-b border-slate-700/50 bg-slate-800/20">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center text-xs text-slate-500">
          <span className="inline-block w-2 h-2 rounded-full bg-slate-600 mr-2" />
          Index data unavailable
        </div>
      </div>
    );
  }

  return (
    <div className="h-8 border-b border-slate-700/50 bg-slate-800/20">
      <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center gap-5 text-xs overflow-x-auto">
        {state.quotes.map((q) => (
          <IndexCell key={q.ticker} quote={q} />
        ))}
        <span className="flex-1" />
        {state.delayed && (
          <span className="hidden sm:inline text-[10px] uppercase tracking-wide text-slate-500">
            Delayed
          </span>
        )}
        {state.asOf && (
          <span className="hidden sm:inline text-[10px] tracking-wide text-slate-500 tabular-nums">
            as of {formatAsOf(state.asOf)}
          </span>
        )}
      </div>
    </div>
  );
}
